import { useMemo, useState } from "react";
import { recordedLabel } from "../../cockpit/format";
import { PART_LABELS } from "./designationParts";
import { codesInSlot, filterApprovedCodes, SHARED_CODE_TABS, sharedCodes, type RegistrySlotName } from "./registryFilter";
import type { ApprovedCodeView } from "../types";

interface Props {
  approvedCodes: ApprovedCodeView[];
}

const HEADER_CELL_CLASSES =
  "bg-rs-black px-3 py-2.5 text-left text-[11.5px] font-medium uppercase tracking-wider text-rs-light-gray border-b border-rs-jet whitespace-nowrap";
const CELL_CLASSES = "px-3 py-2.5 align-top border-t border-rs-jet";

export function SharedCodesTable({ approvedCodes }: Props) {
  const [filterText, setFilterText] = useState("");
  const [selectedSlotName, setSelectedSlotName] = useState<RegistrySlotName>("Filler");
  const allSharedCodes = useMemo(() => sharedCodes(approvedCodes), [approvedCodes]);
  const matchingCodes = useMemo(() => filterApprovedCodes(allSharedCodes, filterText), [allSharedCodes, filterText]);
  const visibleCodes = codesInSlot(matchingCodes, selectedSlotName);
  const selectedTabLabel = SHARED_CODE_TABS.find((tab) => tab.slotName === selectedSlotName)?.label ?? selectedSlotName;
  const isFiltering = filterText.trim() !== "";

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Kind of code">
        {SHARED_CODE_TABS.map((tab) => {
          const isSelected = selectedSlotName === tab.slotName;
          return (
            <button
              key={tab.slotName}
              type="button"
              aria-pressed={isSelected}
              className={`bg-transparent border rounded-full px-2.5 py-0.5 text-xs cursor-pointer ${
                isSelected
                  ? "border-rs-green text-rs-green"
                  : "border-rs-jet text-rs-light-gray hover:border-rs-green hover:text-rs-green"
              }`}
              onClick={() => setSelectedSlotName(tab.slotName)}
            >
              {tab.label} <span className="opacity-80">{codesInSlot(matchingCodes, tab.slotName).length}</span>
            </button>
          );
        })}
      </div>
      <input
        className="ctl max-w-90 text-[13px] py-1.5"
        placeholder="Search code or Reads As"
        aria-label="Search the shared codes"
        value={filterText}
        onChange={(event) => setFilterText(event.target.value)}
      />
      <div className="overflow-x-auto rounded-md border border-rs-jet">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              <th className={HEADER_CELL_CLASSES}>Code</th>
              <th className={HEADER_CELL_CLASSES}>Kind</th>
              <th className={HEADER_CELL_CLASSES}>Reads As</th>
              <th className={HEADER_CELL_CLASSES}>Approved by</th>
            </tr>
          </thead>
          <tbody>
            {visibleCodes.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-3 text-[#a9b0a1]">
                  {allSharedCodes.length === 0
                    ? "Codes are added here when an Uploader or Admin approves them in Pending Codes."
                    : isFiltering
                      ? `No ${selectedTabLabel} codes match "${filterText.trim()}".`
                      : `No ${selectedTabLabel} codes approved yet.`}
                </td>
              </tr>
            )}
            {visibleCodes.map((approvedCode) => (
              <tr key={`${approvedCode.standardCode}|${approvedCode.slotName}|${approvedCode.code}`}>
                <td className={CELL_CLASSES}>
                  <code className="text-rs-green">{approvedCode.code}</code>
                </td>
                <td className={`${CELL_CLASSES} text-rs-off-white`}>{PART_LABELS[approvedCode.slotName]}</td>
                <td className={`${CELL_CLASSES} text-rs-off-white`}>{approvedCode.readsAs}</td>
                <td className={`${CELL_CLASSES} text-[#a9b0a1]`}>
                  {approvedCode.approvedByDisplayName} · {recordedLabel(approvedCode.approvedUtc)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
